import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Breadcrumb from "@/components/site/Breadcrumb";
import { setPageMeta } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { ArrowUp, MessageCircle, Plus, Pin } from "lucide-react";
import { toast } from "sonner";
import { promptLogin, promptRegister } from "@/lib/authRedirect";

export default function SubForum() {
  const { subSlug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/forum/sub-forums/${subSlug}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [subSlug]);

  useEffect(() => {
    if (!data || data.error) return;
    const { category, sub_forum, threads } = data;
    setPageMeta({
      title: `${sub_forum.label} · Forum ${category?.label || ""} — The School Reports`,
      description: (sub_forum.description || `Diskusi orang tua tentang ${sub_forum.label}. Baca ${threads.length} thread & bagikan pengalaman Anda di forum The School Reports.`).slice(0, 300),
      type: "website",
    });
  }, [data]);

  const submitThread = async (e) => {
    e.preventDefault();
    if (!user) { promptLogin(nav); return; }
    if (!form.title.trim() || !form.body.trim()) { toast.error("Isi judul & isi diskusi"); return; }
    try {
      setBusy(true);
      await api.post("/threads", {
        title: form.title, body: form.body,
        category: data.category?.slug || "umum",
        sub_forum_id: data.sub_forum.sub_forum_id,
      });
      toast.success("Diskusi terkirim");
      setOpen(false);
      setForm({ title: "", body: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="max-w-4xl mx-auto p-10 text-[#4B5563]">Memuat…</div>;
  if (data.error) return <div className="max-w-4xl mx-auto p-10 text-[#4B5563]">Sub-forum tidak ditemukan.</div>;

  const { category, sub_forum, threads } = data;

  return (
    <div data-testid="sub-forum-page" className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
      <Breadcrumb testid="subforum-breadcrumb" items={[
        { label: "Forum", to: "/forum" },
        { label: category?.label || "Kategori", to: `/forum?cat=${category?.slug || ""}` },
        { label: sub_forum.label },
      ]} />

      <div className="mt-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#4B5563]">{category?.label}</div>
          <h1 className="font-sans font-bold text-3xl lg:text-5xl leading-tight text-[#1F2937] mt-1 tracking-tight">{sub_forum.label}</h1>
          {sub_forum.description && <p className="mt-2 text-[#4B5563] max-w-2xl leading-relaxed">{sub_forum.description}</p>}
        </div>
        <Button onClick={() => (user ? setOpen(true) : promptLogin(nav))} data-testid="new-thread-btn" className="rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white">
          <Plus size={14} className="mr-1" /> Mulai diskusi
        </Button>
      </div>

      <div className="mt-8 space-y-2">
        {threads.length === 0 && <div className="text-center text-[#4B5563] p-10 rounded-2xl border border-dashed border-[#EAE7E0]" data-testid="sub-forum-empty">Belum ada diskusi di sub-forum ini. Jadilah yang pertama!</div>}
        {threads.map((t, i) => (
          <Link key={t.thread_id} to={`/forum/${t.thread_id}`} data-testid={`sub-thread-${i}`} className="block bg-white border border-[#EAE7E0] rounded-2xl p-4 hover:border-[#1E3A8A] hover:-translate-y-0.5 transition-all group">
            <div className="flex items-start gap-4">
              <div className="flex flex-col items-center gap-0.5 min-w-[42px] text-[#1E3A8A] pt-0.5">
                <ArrowUp size={16} strokeWidth={2.5} />
                <span className="font-semibold text-sm">{t.upvotes}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-sans font-semibold text-base text-[#1F2937] group-hover:text-[#1E3A8A] transition-colors leading-snug">{t.title} {t.pinned && <Pin size={13} className="inline text-[#E07A5F] ml-1" />}</div>
                <p className="mt-1 text-sm text-[#4B5563] line-clamp-2">{t.body}</p>
                <div className="mt-2 text-xs text-[#4B5563] flex items-center gap-3">
                  <span>oleh {t.user_name}</span>
                  <span className="inline-flex items-center gap-1"><MessageCircle size={11} /> {t.reply_count} balasan</span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mulai diskusi di {sub_forum.label}</DialogTitle></DialogHeader>
          <form onSubmit={submitThread} className="space-y-3">
            <Input placeholder="Judul diskusi" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sub-thread-title" required />
            <Textarea rows={5} placeholder="Isi diskusi" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="sub-thread-body" required />
            <Button type="submit" disabled={busy} data-testid="sub-thread-submit" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
              {busy ? "Mengirim…" : "Kirim"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
